import { useState, useRef, useEffect, useMemo } from "react";
import { Palette, X, ChevronDown } from "lucide-react";

// Sélecteur d'illustrateur : liste déroulante plutôt que saisie libre — il y en
// a plus d'un millier et personne ne connaît l'orthographe exacte.
// Appuyer sur une lettre saute directement aux illustrateurs de cette lettre
// (comme dans un répertoire), et la bande A-Z sur le côté fait la même chose au
// clic. Un champ de filtre reste disponible pour les recherches précises.
export const IllustratorPicker = ({ illustrators, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const boxRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const sorted = useMemo(
    () => [...illustrators].sort((a, b) => a.localeCompare(b, "fr")),
    [illustrators]
  );

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? sorted.filter(i => i.toLowerCase().includes(q)) : sorted;
  }, [sorted, filter]);

  // Lettres réellement présentes, pour ne pas proposer des touches mortes.
  const letters = useMemo(() => {
    const set = new Set(shown.map(i => i[0]?.toUpperCase()).filter(Boolean));
    return [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"].filter(l => set.has(l));
  }, [shown]);

  const jumpTo = (letter) => {
    const node = listRef.current?.querySelector(`[data-letter="${letter}"]`);
    if (node) node.scrollIntoView({ block: "start" });
  };

  // Frappe d'une lettre = saut, sans passer par le champ de filtre.
  const onKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.target.tagName === "INPUT") return;
    if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) jumpTo(e.key.toUpperCase());
  };

  let lastLetter = null;

  return (
    <div className="relative" ref={boxRef} onKeyDown={onKeyDown} tabIndex={-1}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-left">
        <Palette className="w-4 h-4 text-zinc-500 shrink-0" />
        <span className={`flex-1 truncate ${value ? "text-white" : "text-zinc-500"}`}>
          {value || "Illustrateur…"}
        </span>
        {value && (
          <X className="w-3.5 h-3.5 text-zinc-500 hover:text-white shrink-0"
            onClick={(e) => { e.stopPropagation(); onChange(""); }} />
        )}
        <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl z-50">
          <div className="p-2 border-b border-zinc-800">
            <input autoFocus value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrer…"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-white placeholder:text-zinc-600 focus:outline-none" />
          </div>
          <div className="flex">
            <div ref={listRef} className="flex-1 max-h-72 overflow-y-auto py-1">
              {shown.length === 0 && (
                <p className="text-sm text-zinc-600 px-3 py-3">Aucun illustrateur.</p>
              )}
              {shown.map(name => {
                const letter = name[0]?.toUpperCase();
                const isNew = letter !== lastLetter;
                if (isNew) lastLetter = letter;
                return (
                  <button key={name}
                    {...(isNew ? { "data-letter": letter } : {})}
                    onClick={() => { onChange(name); setOpen(false); }}
                    className={`w-full text-left text-sm px-3 py-1 hover:bg-zinc-800 ${
                      name === value ? "text-emerald-400" : "text-zinc-300"}`}>
                    {isNew && <span className="text-[0.6rem] text-zinc-600 mr-1.5">{letter}</span>}
                    {name}
                  </button>
                );
              })}
            </div>
            <div className="w-6 shrink-0 border-l border-zinc-800 py-1 overflow-y-auto max-h-72 text-center">
              {letters.map(l => (
                <button key={l} onClick={() => jumpTo(l)}
                  className="block w-full text-[0.6rem] text-zinc-500 hover:text-white leading-4">
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
